import { useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { useSfActivityLoader } from "../../hooks/salesforce/useSfActivityLoader";
import type { SfObject } from "../../store/insightLinkage";
import { SfObjectType } from "../../store/insightLinkage";
import { notificationStore, NotificationType } from "../../store/notification";
import type { ContentAction } from "../../types/content";
import { isLoading, isSuccess } from "../../utils/api.utils";
import { hasSfObjectContext } from "../../utils/permission.utils";

import type { DropdownComponentProps } from "./Dropdown";
import { DropdownComponent } from "./Dropdown";
import { filterItemByValueAndLabel } from "./DropdownHelpers";

export interface ActivityDropdownProps {
    sfObject: SfObject;
    setSfObject: (sfObjectData: SfObject) => void;
    startDate: string;
    endDate: string;
    contentAction: ContentAction;
    closeModal?: () => void;
}

export const ActivityDropdown: React.FunctionComponent<
    ActivityDropdownProps
> = (props: ActivityDropdownProps) => {
    const {
        sfObject,
        setSfObject,
        startDate,
        endDate,
        contentAction,
        closeModal,
    } = props;

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [sfActivityState, activityDropdownItems] = useSfActivityLoader({
        startDate,
        endDate,
        contentAction,
    });
    const [filter, setFilter] = useState("");

    const sfObjectContextExists = hasSfObjectContext();

    const filteredItems = useMemo(
        () => filterItemByValueAndLabel(activityDropdownItems, filter),
        [filter, sfActivityState],
    );

    const onActivityDropdownChange = (selectedItem: string) => {
        const { value: activityIdLookup } = activityDropdownItems.find(
            (item) => item.value === selectedItem,
        ) ?? { value: selectedItem };

        setSfObject({
            sfObjectType: SfObjectType.ACTIVITY,
            sfObjectId: activityIdLookup,
        });
    };

    useEffect(() => {
        if (
            sfObjectContextExists &&
            isSuccess(sfActivityState) &&
            sfObject.sfObjectType === SfObjectType.ACTIVITY
        ) {
            const targetSFObjectId = activityDropdownItems.find(
                (item) => item.value === sfObject.sfObjectId,
            );
            if (!targetSFObjectId) {
                upsertNotification(
                    "Invalid activity selected",
                    NotificationType.ERROR,
                );
                closeModal?.();
            }
        }
    }, [sfActivityState, sfObject]);

    const dropdownProps: DropdownComponentProps = {
        dimension: "activity",
        label: "Salesforce Activity",
        items: filteredItems,
        search: filter,
        onChange: (e) => {
            onActivityDropdownChange(e);
        },
        selectedValue: sfObject.sfObjectId,
        setSearch: (e) => {
            setFilter(e);
        },
        loading: isLoading(sfActivityState),
        showItemsWhileLoading: sfActivityState === "loading",
        disabled: sfObjectContextExists,
    };

    return <DropdownComponent {...dropdownProps} />;
};

export default ActivityDropdown;
